"use strict";
/*
 * Copyright 2023-2024 The NATS Authors
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.RepublishHeaders = exports.DirectMsgHeaders = exports.JsHeaders = exports.AdvisoryKind = void 0;
exports.isOrderedPushConsumerOptions = isOrderedPushConsumerOptions;
exports.isPullConsumer = isPullConsumer;
exports.isPushConsumer = isPushConsumer;
exports.isBoundPushConsumerOptions = isBoundPushConsumerOptions;
function isOrderedPushConsumerOptions(v) {
    if (v && typeof v === "object") {
        return "name_prefix" in v ||
            "deliver_subject_prefix" in v ||
            "filter_subjects" in v ||
            "filter_subject" in v ||
            "deliver_policy" in v ||
            "opt_start_seq" in v ||
            "opt_start_time" in v ||
            "replay_policy" in v ||
            "inactive_threshold" in v ||
            "headers_only" in v ||
            "deliver_prefix" in v;
    }
    return false;
}
function isPullConsumer(v) {
    return v.isPullConsumer();
}
function isPushConsumer(v) {
    return v.isPushConsumer();
}
function isBoundPushConsumerOptions(v) {
    if (v && typeof v === "object") {
        return "deliver_subject" in v ||
            "deliver_group" in v ||
            "idle_heartbeat" in v;
    }
    return false;
}
/**
 * The different kinds of Advisories
 */
var AdvisoryKind;
(function (AdvisoryKind) {
    AdvisoryKind["API"] = "api_audit";
    AdvisoryKind["StreamAction"] = "stream_action";
    AdvisoryKind["ConsumerAction"] = "consumer_action";
    AdvisoryKind["SnapshotCreate"] = "snapshot_create";
    AdvisoryKind["SnapshotComplete"] = "snapshot_complete";
    AdvisoryKind["RestoreCreate"] = "restore_create";
    AdvisoryKind["RestoreComplete"] = "restore_complete";
    AdvisoryKind["MaxDeliver"] = "max_deliver";
    AdvisoryKind["Terminated"] = "terminated";
    AdvisoryKind["Ack"] = "consumer_ack";
    AdvisoryKind["StreamLeaderElected"] = "stream_leader_elected";
    AdvisoryKind["StreamQuorumLost"] = "stream_quorum_lost";
    AdvisoryKind["ConsumerLeaderElected"] = "consumer_leader_elected";
    AdvisoryKind["ConsumerQuorumLost"] = "consumer_quorum_lost";
})(AdvisoryKind || (exports.AdvisoryKind = AdvisoryKind = {}));
var JsHeaders;
(function (JsHeaders) {
    /**
     * Set if message is from a stream source - format is `stream seq`
     */
    JsHeaders["StreamSourceHdr"] = "Nats-Stream-Source";
    /**
     * Set for heartbeat messages
     */
    JsHeaders["LastConsumerSeqHdr"] = "Nats-Last-Consumer";
    /**
     * Set for heartbeat messages
     */
    JsHeaders["LastStreamSeqHdr"] = "Nats-Last-Stream";
    /**
     * Set for heartbeat messages if the consumer is stalled
     */
    JsHeaders["ConsumerStalledHdr"] = "Nats-Consumer-Stalled";
    /**
     * Set for headers_only consumers indicates the number of bytes in the payload
     */
    JsHeaders["MessageSizeHdr"] = "Nats-Msg-Size";
    // rollup header
    JsHeaders["RollupHdr"] = "Nats-Rollup";
    // value for rollup header when rolling up a subject
    JsHeaders["RollupValueSubject"] = "sub";
    // value for rollup header when rolling up all subjects
    JsHeaders["RollupValueAll"] = "all";
    /**
     * Set on protocol messages to indicate pull request message count that
     * was not honored.
     */
    JsHeaders["PendingMessagesHdr"] = "Nats-Pending-Messages";
    /**
     * Set on protocol messages to indicate pull request byte count that
     * was not honored
     */
    JsHeaders["PendingBytesHdr"] = "Nats-Pending-Bytes";
})(JsHeaders || (exports.JsHeaders = JsHeaders = {}));
var DirectMsgHeaders;
(function (DirectMsgHeaders) {
    DirectMsgHeaders["Stream"] = "Nats-Stream";
    DirectMsgHeaders["Sequence"] = "Nats-Sequence";
    DirectMsgHeaders["TimeStamp"] = "Nats-Time-Stamp";
    DirectMsgHeaders["Subject"] = "Nats-Subject";
    DirectMsgHeaders["LastSequence"] = "Nats-Last-Sequence";
})(DirectMsgHeaders || (exports.DirectMsgHeaders = DirectMsgHeaders = {}));
var RepublishHeaders;
(function (RepublishHeaders) {
    /**
     * The source stream of the message
     */
    RepublishHeaders["Stream"] = "Nats-Stream";
    /**
     * The original subject of the message
     */
    RepublishHeaders["Subject"] = "Nats-Subject";
    /**
     * The sequence of the republished message
     */
    RepublishHeaders["Sequence"] = "Nats-Sequence";
    /**
     * The stream sequence id of the last message ingested to the same original subject (or 0 if none or deleted)
     */
    RepublishHeaders["LastSequence"] = "Nats-Last-Sequence";
    /**
     * The size in bytes of the message's body - Only if {@link Republish#headers_only} is set.
     */
    RepublishHeaders["Size"] = "Nats-Msg-Size";
})(RepublishHeaders || (exports.RepublishHeaders = RepublishHeaders = {}));
//# sourceMappingURL=types.js.map